import React, { useState } from 'react'

import TrendText from 'components_new/molecules/TrendText'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import DetailsDialog from '../../dialogs/DateDetailsDialog'

import Lock from './Lock'
import HistoricalDataOutOfBounce from './HistoricalDataOutOfBounce'

import {
  CustomPeriodFilter,
  DateFilterType,
  isDashboardWidgetFullyLoaded,
  KeyFigureVisualizationType,
  PeriodFilter,
  TCoreWidgetSettingsLoaded,
  WidgetType
} from 'types/GlobalWidget'

import {
  getKpiOptionsSettings,
  TParsedKpiValues
} from '../../Types/KeyFigure/utils'

import {
  getComparativePeriodLabel,
  getDisplayedPeriod,
  getPeriodLabel
} from './utils'

import { getParsedDate, getParsedDateSpan } from 'utils/dateParser'
import ExtraLongForecastMissingSupportedKpi from './ExtraLongForecastMissingSupportedKpi'
import WidgetWarning from './WidgetWarning'
import { useTranslation } from 'language/translator'
import { Loading } from './loading'
import { useCurrencyConverter } from 'redux/hooks/CurrencyConverter'
import { useWidgetFetchError } from 'redux/hooks/Widgets'

interface DateFilterSectionProps {
  dashboardCustomPeriodFilter: CustomPeriodFilter | null
  dashboardPeriodFilter: PeriodFilter | null
  embedded: boolean
  interactive: boolean
  scaleFactor: number
  widget: TCoreWidgetSettingsLoaded
}

const DateFilterSection = (props: DateFilterSectionProps) => {
  const {
    dashboardCustomPeriodFilter,
    dashboardPeriodFilter,
    embedded,
    interactive,
    scaleFactor,
    widget
  } = props

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const { t, translateEnum, lang } = useTranslation()
  const currency = useCurrencyConverter()
  const fetchError = useWidgetFetchError(widget.id)

  const widgetType = widget.type

  // Settings
  const isLocked = widget.settings.date_filter_is_locked
  const dateFilterType = widget.settings.date_filter.type
  const rollingActive = widget.settings.date_filter.rolling_active
  const isPoint =
    (dateFilterType === DateFilterType.POINT || Boolean(rollingActive)) &&
    widgetType !== WidgetType.LINE_CHART &&
    widget.settings.segment_by.type !== 'DATE'
  const isKeyFigure = widgetType === WidgetType.KEY_FIGURE
  const showDates = widget.settings.show_date
  const widgetPeriodFilter = widget.settings.date_filter.selected?.value

  if (
    !dashboardCustomPeriodFilter &&
    !dashboardPeriodFilter &&
    !widgetPeriodFilter
  ) {
    return null
  }

  const displayedPeriod = getDisplayedPeriod(
    dashboardCustomPeriodFilter,
    dashboardPeriodFilter,
    isLocked,
    widgetPeriodFilter
  )

  // Forecast
  const forecastPeriod = widget.settings.forecast_period.selected

  // Period
  const periodFromDate = widget.settings.date_filter.selected.from_date
  const periodToDate = widget.settings.date_filter.selected.to_date
  const restrictToPeriod =
    widget.settings.date_filter.restrict_to_period.selected

  const isFinancialYear = widget.settings.date_filter.selected.is_financial_year

  const periodLabel = isPoint
    ? getParsedDate(periodFromDate, lang)
    : getPeriodLabel(
      displayedPeriod.filter,
      displayedPeriod.type,
      forecastPeriod,
      restrictToPeriod,
      t,
      translateEnum
    )

  // Comparative period
  const comparativePeriod = widget.settings.comparative_period.selected.value
  const comparativePeriodFromDate =
    widget.settings.comparative_period.selected.from_date
  const comparativePeriodToDate =
    widget.settings.comparative_period.selected.to_date

  const comparativePeriodLabel = getComparativePeriodLabel(
    displayedPeriod.filter,
    displayedPeriod.type,
    comparativePeriod,
    comparativePeriodFromDate,
    comparativePeriodToDate,
    isPoint,
    forecastPeriod,
    t,
    translateEnum
  )

  const showComparativePeriod = Boolean(comparativePeriod)

  const dateSpan = getParsedDateSpan(periodFromDate, periodToDate)
  const comparativeDateSpan = getParsedDateSpan(
    comparativePeriodFromDate,
    comparativePeriodToDate
  )

  // show dates for financial year date filters instead of labels
  const periodFooterLabel = isFinancialYear ? dateSpan : periodLabel
  const comparativePeriodFooterLabel = isFinancialYear
    ? comparativeDateSpan
    : comparativePeriodLabel

  // Trend for KPI box
  let kpiBoxOptions: TParsedKpiValues[] = []

  if (isKeyFigure) {
    kpiBoxOptions = getKpiOptionsSettings(widget, currency)
  }

  const hasError = fetchError !== undefined || widget.status.broken

  if (!isDashboardWidgetFullyLoaded(widget) && !hasError) {
    return (
      <Loading
        scaleFactor={scaleFactor}
        showComparativePeriod={showComparativePeriod}
      />
    )
  }

  if (hasError) {
    return null
  }

  return (
    <>
      {showDates || (isKeyFigure && comparativePeriod) ? (
        <>
          <Box
            onClick={interactive ? () => setDialogOpen(true) : () => {}}
            sx={{
              color: 'text.secondary',
              cursor: interactive ? 'pointer' : undefined,
              fontSize: 14 * scaleFactor,
              '&:hover': interactive
                ? {
                    color: 'text.primary',
                    '& .date-filter-section-arrow': {
                      display: 'flex'
                    },
                    '& .date-filter-section-bg': {
                      display: 'block'
                    },
                    paddingRight: `calc(${4 * scaleFactor}px + 1.5em)`
                  }
                : undefined,
              mt: scaleFactor,
              position: 'relative'
            }}
          >
            {/*-- Selected period + Forecast period --*/}
            {showDates ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <Text
                  color="inherit"
                  fontSize="inherit"
                  fontWeight="medium"
                  noWrap={true}
                >
                  {periodFooterLabel}
                </Text>

                {/*-- Lock --*/}
                {isLocked ? <Lock /> : null}
              </Box>
            ) : null}

            {/*-- Comparative period --*/}
            {showComparativePeriod ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <Text color="inherit" fontSize="inherit" noWrap={true}>
                  {/*-- Optional key figure trend --*/}
                  {isKeyFigure &&
                  widget.settings.key_figure_visualization_type ===
                    KeyFigureVisualizationType.FOCUS &&
                  kpiBoxOptions.length === 1 ? (
                        <>
                          <TrendText
                            equivalentFactor={kpiBoxOptions[0].equivalentFactor}
                            difference={kpiBoxOptions[0].difference}
                            // eslint-disable-next-line max-len
                            increaseIsPositive={kpiBoxOptions[0].increaseIsPositive}
                            numberOfDecimals={
                              kpiBoxOptions[0].numberOfDecimals || 0
                            }
                            percentage={kpiBoxOptions[0].percentage || 0}
                            // eslint-disable-next-line max-len
                            significantFigures={kpiBoxOptions[0].significantFigures}
                            unitPrefix={kpiBoxOptions[0].unitPrefixSelected}
                          />{' '}
                        </>
                      ) : null}
                  {showDates ? (
                    <>
                      <Text
                        color="inherit"
                        component="span"
                        fontSize="inherit"
                        fontWeight="regular"
                      >
                        {t('global', 'compare_with_short', true) + ' '}
                      </Text>
                      <Text
                        color="inherit"
                        component="span"
                        fontSize="inherit"
                        fontWeight="medium"
                      >
                        {comparativePeriodFooterLabel?.toLowerCase()}
                      </Text>
                    </>
                  ) : null}
                </Text>

                {/*-- Lock --*/}
                {isLocked ? <Lock /> : null}
              </Box>
            ) : null}

            {/*-- Hover arrow --*/}
            <Box
              className="date-filter-section-arrow"
              sx={{
                alignItems: 'center',
                display: 'none',
                height: '100%',
                position: 'absolute',
                right: 0,
                top: 0,
                pr: `${4 * scaleFactor}px`
              }}
            >
              <Icon color="inherit" name="Info" sx={{ fontSize: '1.5em' }} />
            </Box>

            {/*-- Hover background --*/}
            <Box
              className="date-filter-section-bg"
              sx={{
                display: 'none',
                height: `calc(100% + ${4 * scaleFactor}px)`,
                width: `calc(100% + ${8 * scaleFactor}px)`,
                position: 'absolute',
                top: `-${2 * scaleFactor}px`,
                left: `-${4 * scaleFactor}px`,
                bgcolor: 'action.hover',
                zIndex: 0
              }}
            />
          </Box>

          {/*-- Dialog --*/}
          <DetailsDialog
            comparativePeriodFromDate={comparativePeriodFromDate}
            comparativePeriodLabel={comparativePeriodLabel}
            comparativePeriodToDate={comparativePeriodToDate}
            dateFilter={displayedPeriod.filter}
            isLocked={isLocked}
            isPoint={isPoint}
            onClose={() => setDialogOpen(false)}
            open={dialogOpen}
            periodFromDate={periodFromDate}
            periodLabel={periodLabel}
            periodToDate={periodToDate}
            showComparativePeriod={showComparativePeriod}
          />
        </>
      ) : (
        <></>
      )}
      <HistoricalDataOutOfBounce
        embedded={embedded}
        scaleFactor={scaleFactor}
        widget={widget}
      />
      <ExtraLongForecastMissingSupportedKpi
        scaleFactor={scaleFactor}
        widget={widget}
      />
      <WidgetWarning
        embedded={embedded}
        flag="is_latest_exchange_rate"
        labelKey="latest_exchange_rate_warning"
        scaleFactor={scaleFactor}
        tooltipKey="latest_exchange_rate_tooltip"
        widget={widget}
      />
      <WidgetWarning
        embedded={embedded}
        flag="is_weighted_average"
        labelKey="weighted_average_warning"
        scaleFactor={scaleFactor}
        tooltipKey="weighted_average_tooltip"
        widget={widget}
      />
    </>
  )
}

export default DateFilterSection
